<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TimeRange, TimeRangeKey } from '@/types/cockpit'

const props = defineProps<{
  modelValue: TimeRange
}>()

const emit = defineEmits<{
  'update:modelValue': [value: TimeRange]
}>()

const { t } = useI18n()

const presets: { key: TimeRangeKey; label: string }[] = [
  { key: '7d', label: t('admin.cockpit.range.7d') },
  { key: '30d', label: t('admin.cockpit.range.30d') },
  { key: 'quarter', label: t('admin.cockpit.range.quarter') },
  { key: 'ytd', label: t('admin.cockpit.range.ytd') },
  { key: 'custom', label: t('admin.cockpit.range.custom') },
]

const customFrom = ref(props.modelValue.from ?? '')
const customTo = ref(props.modelValue.to ?? '')

const isCustomValid = ref(true)

watch([customFrom, customTo], ([from, to]) => {
  if (!from || !to) {
    isCustomValid.value = false
    return
  }
  isCustomValid.value = new Date(to) >= new Date(from)
})

function selectPreset(key: TimeRangeKey): void {
  if (key !== 'custom') {
    emit('update:modelValue', { key })
  } else {
    emit('update:modelValue', { key: 'custom', from: customFrom.value, to: customTo.value })
  }
}

function applyCustom(): void {
  if (!isCustomValid.value) return
  emit('update:modelValue', { key: 'custom', from: customFrom.value, to: customTo.value })
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <div class="flex items-center bg-slate-800 border border-slate-700 rounded-lg p-1 gap-1">
      <button
        v-for="preset in presets"
        :key="preset.key"
        type="button"
        class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
        :class="
          modelValue.key === preset.key
            ? 'bg-emerald-600 text-white'
            : 'text-slate-400 hover:text-white hover:bg-slate-700'
        "
        @click="selectPreset(preset.key)"
      >
        {{ preset.label }}
      </button>
    </div>

    <template v-if="modelValue.key === 'custom'">
      <div class="flex items-center gap-2">
        <input
          v-model="customFrom"
          type="date"
          class="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          :aria-label="t('admin.cockpit.range.from')"
        />
        <span class="text-slate-500 text-sm">{{ t('admin.cockpit.range.to_label') }}</span>
        <input
          v-model="customTo"
          type="date"
          class="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          :aria-label="t('admin.cockpit.range.to')"
        />
        <button
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="!isCustomValid"
          @click="applyCustom"
        >
          {{ t('admin.cockpit.range.apply') }}
        </button>
      </div>
    </template>
  </div>
</template>
